/**
 * Relewise ORDERING for Shopify-rendered product rows and grids.
 *
 * The SET of products stays whatever Shopify rendered (a collection, a curated
 * list, a paginated PLP page); Relewise only decides the ORDER — which is the
 * "data-source swap behind a designed element" the Figma rule allows. Three
 * surfaces share this file:
 *
 *   sections/section-tab-collections.liquid   homepage "Nyheder" rows   (personal | popular, per collection)
 *   sections/spejder-member-dashboard.liquid  kundeklub "Anbefalet til dig" (personal, whole catalog)
 *   sections/collection.liquid                PLP default view         (relevance via termless ProductSearch)
 *   sections/collection.liquid                PLP filtered view        (sort via SortProducts on the page's own cards)
 *
 * Contract — one [data-relewise-order] scope per row/grid:
 *   data-relewise-order   personal | popular | relevance | sort
 *   data-collection-id    Shopify collection id = Relewise category id (app mapping).
 *                         Omit for an unfiltered request (dashboard).
 *   data-take             how many ranked handles to ask for (defaults to the card count)
 *   data-page             1-based page, `relevance` only (PLP pagination offset)
 *   data-days             popular window in days (default 30)
 *   data-sort             `relevance` only: popularity → termless search sorted by
 *                         popularity (then relevance), still paged like Shopify
 *   data-restrict         page → `popular` ranks only the cards on the page
 *                         (ProductIdFilter on their data-relewise-id), no fetch
 *   data-location         Relewise "Displayed At Location" for the analytics
 *
 * Algorithm: take Relewise's ranked handles first, then every Shopify card
 * whose handle Relewise did not name, cut to the card count Shopify rendered.
 * Cards already on the page are MOVED (no fetch); a handle Relewise ranks that
 * Shopify did not render on this page/row is fetched as ?view=relewise-card.
 * Card count, breaker cells (PLP) and the featured-image cell (tab rows) are
 * untouched, so page maths and the grid layout never change.
 *
 * Nothing here is fatal: no consent, Relewise down, empty result, unknown
 * handle — the Shopify order simply stays.
 *
 * No flash of Shopify's order: the cards of a scope stay invisible (CSS in
 * spejder.css) until the scope carries data-relewise-ready, which this script
 * sets once the ranking is applied, skipped or failed; a CSS failsafe reveals
 * them after 2.5 s regardless and a ranking arriving after RENDER_BUDGET_MS
 * is dropped so it never reshuffles an already visible grid.
 *
 * PLP caveat (documented, accepted): in `relevance` mode Relewise ranks the
 * WHOLE collection and Shopify paginates its own order, so page N's Shopify
 * set and Relewise's page-N ranking can differ slightly; the tail-fill keeps
 * every page full and duplicate-free within itself, but a product may in rare
 * cases appear on two pages or on none. The full Relewise-powered PLP (facets
 * phase) removes this.
 *
 * `sort` mode (filtered PLP views, 2026-09-09): Relewise cannot mirror
 * Shopify's facet selection, so instead of asking for the collection it ranks
 * exactly the cards Shopify rendered — their Relewise product ids
 * (data-relewise-id = custom.relewise_id) go to SortProducts and come back in
 * personalized order (personal history + popularity + merchandising rules).
 * Only handles already on the page are accepted (no fetch, no fill), so the
 * set is provably unchanged. Merchandising rules scoped to recommendations
 * apply: a product they filter out (e.g. sold out) is simply not ranked and
 * keeps Shopify's relative order at the tail.
 */
!(function () {
  'use strict';

  /* Unclaimed scopes only — a scope is ordered once. The PLP grid is
     re-rendered by the theme's AJAX filtering (collection-filters-form swaps
     [data-products-grid]'s innerHTML), and the fresh markup carries
     [data-relewise-order] again whenever the new URL is the untouched
     default view (e.g. after "Ryd alle"), so claim() runs again on every
     grid swap and the default view keeps its Relewise order without a
     page load (2026-09-08). */
  function claim() {
    return [].slice.call(document.querySelectorAll('[data-relewise-order]')).filter(function (el) {
      if (el.hasAttribute('data-relewise-order-claimed')) return false;
      el.setAttribute('data-relewise-order-claimed', '');
      return true;
    });
  }

  /* Cards in an unclaimed scope are hidden by CSS (spejder.css) until the
     scope carries data-relewise-ready; the CSS failsafe reveals them by
     itself after 2.5 s. A ranking that arrives later than this budget is
     dropped rather than applied on top of the already visible Shopify order
     — the flash it would cause is the very thing the hold prevents. */
  var RENDER_BUDGET_MS = 2000;

  function ready(scope) { scope.setAttribute('data-relewise-ready', ''); }

  var cfg = window.spejder && window.spejder.relewise;
  if (!cfg || !cfg.enabled || !window.Relewise) {
    [].slice.call(document.querySelectorAll('[data-relewise-order]')).forEach(ready);
    return;
  }

  var handleKey = cfg.shop + '_ShopifyHandle';

  /* Consent-correct identity via the shared helper (assets/relewise-batch.js),
     which waits for the app embed's global instead of testing for it now. */
  var userPromise = window.spejder.relewiseUser
    ? window.spejder.relewiseUser()
    : Promise.resolve(Relewise.UserFactory.anonymous());

  /* Product cards only — the tab row's featured-image cell and the PLP's
     breaker cells are [data-grid-item] too but carry no product. */
  function cardsOf(scope) {
    return [].slice.call(scope.querySelectorAll('[data-grid-item][data-url]'))
      .filter(function (el) { return !el.classList.contains('product-item--featured'); });
  }

  function handleOf(card) {
    var url = card.getAttribute('data-url') || '';
    return url.split('?')[0].split('/products/')[1] || '';
  }

  function settingsFor(user, opt) {
    return { language: cfg.language, currency: cfg.currency, displayedAtLocation: opt.location, user: user };
  }

  function categoryFilter(opt) {
    return function (f) {
      if (opt.collectionId) f.addProductCategoryIdFilter('ImmediateParent', [opt.collectionId]);
    };
  }

  /* The page's rows (both homepage tab rows, the dashboard row) share ONE
     batched request, deduplicated across rows by Relewise — see
     assets/relewise-batch.js. Direct Recommender call only as the fallback
     when that file did not load; `method` names it. `relevance` is a search
     request and stays on its own Searcher call. */
  function recommend(method, request) {
    if (window.spejder.relewiseBatch) return window.spejder.relewiseBatch.recommend(request);
    return new Relewise.Recommender(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl })[method](request);
  }

  function popular(user, opt) {
    var pageOnly = opt.restrict === 'page';
    if (pageOnly && opt.ids.length < 2) return Promise.resolve([]);
    var b = new Relewise.PopularProductsBuilder(settingsFor(user, opt))
      /* Required — PopularProducts answers nothing without a window. */
      .sinceMinutesAgo(opt.days * 24 * 60)
      .setNumberOfRecommendations(pageOnly ? opt.ids.length : opt.take)
      .setSelectedProductProperties({ dataKeys: [handleKey] })
      .filters(function (f) {
        /* Restricted to the page's own products (filtered PLP, "Mest
           populære"): the category filter is redundant then. */
        if (pageOnly) f.addProductIdFilter(opt.ids);
        else categoryFilter(opt)(f);
      });
    if (pageOnly) b.allowFillIfNecessaryToReachNumberOfRecommendations(false);
    return recommend('recommendPopularProducts', b.build()).then(recHandles);
  }

  function personal(user, opt) {
    var b = new Relewise.PersonalProductRecommendationBuilder(settingsFor(user, opt))
      .setNumberOfRecommendations(opt.take)
      .setSelectedProductProperties({ dataKeys: [handleKey] })
      .filters(categoryFilter(opt));
    return recommend('recommendPersonalProducts', b.build()).then(recHandles);
  }

  /* Termless ProductSearch = Relewise's PLP mode: personalized relevance with
     the merchandising rules from My Relewise applied, paginated like Shopify. */
  function relevance(user, opt) {
    var b = new Relewise.ProductSearchBuilder(settingsFor(user, opt))
      .pagination(function (p) { p.setPageSize(opt.take).setPage(opt.page); })
      .setSelectedProductProperties({ dataKeys: [handleKey] })
      .filters(categoryFilter(opt));
    /* "Mest populære": popularity first, personalized relevance as the
       tie-breaker (Relewise's rule: always chain thenBy → relevance). No
       explicit sorting otherwise — the default IS personalized relevance. */
    if (opt.sort === 'popularity') {
      b.sorting(function (s) {
        s.sortByProductPopularity('Descending', function (t) { t.sortByProductRelevance(); });
      });
    }
    var request = b.build();
    /* One hit per product, same contract as the search page (set post-build:
       the app embed's SDK lacks setVariantRequestSettings — see there). */
    request.settings.variantRequestSettings = { maxVariantsPerProduct: 1, sorting: 'GroupedByProduct' };
    return new Relewise.Searcher(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl })
      .searchProducts(request)
      .then(function (result) { return pluck((result && result.results) || []); });
  }

  /* Personalized order of the page's own cards — the ids come from the
     rendered cards, nothing outside the page can be named. */
  function sort(user, opt) {
    var ids = opt.ids || [];
    if (ids.length < 2) return Promise.resolve([]);
    var b = new Relewise.SortProductsBuilder(settingsFor(user, opt))
      .setProductIds(ids)
      .setNumberOfRecommendations(ids.length)
      .allowFillIfNecessaryToReachNumberOfRecommendations(false)
      .setSelectedProductProperties({ dataKeys: [handleKey] });
    return recommend('sortProducts', b.build()).then(recHandles);
  }

  function recHandles(result) { return pluck((result && result.recommendations) || []); }
  function pluck(items) {
    return items.map(function (item) {
      return item.data && item.data[handleKey] && item.data[handleKey].value;
    }).filter(Boolean);
  }

  function fetchCard(handle) {
    return fetch('/products/' + handle + '?view=relewise-card')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .catch(function () { return ''; })
      .then(function (html) {
        /* A handle Relewise knows but Shopify does not answers 200 with an
           empty shell — only accept a card that actually links to a product. */
        if (!html || html.indexOf('href="/products/') === -1) return null;
        var tpl = document.createElement('template');
        tpl.innerHTML = html.trim();
        return tpl.content.querySelector('[data-grid-item]') || null;
      });
  }

  /* Ranked handles first, Shopify's leftovers after, cut to Shopify's count. */
  function merge(ranked, cards) {
    var byHandle = {};
    cards.forEach(function (c) { byHandle[handleOf(c)] = c; });
    var seen = {};
    var order = [];
    ranked.forEach(function (h) { if (!seen[h]) { seen[h] = true; order.push(h); } });
    cards.forEach(function (c) { var h = handleOf(c); if (!seen[h]) { seen[h] = true; order.push(h); } });
    order = order.slice(0, cards.length);
    return Promise.all(order.map(function (h) {
      return byHandle[h] ? Promise.resolve(byHandle[h]) : fetchCard(h);
    })).then(function (nodes) {
      /* A fetch that came back empty keeps Shopify's own next card instead,
         so the row never loses a cell. */
      var out = nodes.filter(Boolean);
      var i = 0;
      while (out.length < cards.length && i < cards.length) {
        if (out.indexOf(cards[i]) === -1) out.push(cards[i]);
        i++;
      }
      return out;
    });
  }

  function unchanged(cards, nodes) {
    return cards.length === nodes.length && cards.every(function (c, i) { return c === nodes[i]; });
  }

  function render(scope, cards, nodes) {
    if (unchanged(cards, nodes)) return;

    /* Slot-by-slot swap: the product cells keep their positions relative to
       breaker/featured cells. Placeholders first, because a node being moved
       may itself still be one of the later slots. */
    var marks = cards.map(function (c) {
      var m = document.createComment('relewise-order');
      c.replaceWith(m);
      return m;
    });
    marks.forEach(function (m, i) { m.replaceWith(nodes[i]); });

    nodes.forEach(function (n) {
      n.classList.add('aos-animate');
      n.querySelectorAll('[data-aos]').forEach(function (el) { el.classList.add('aos-animate'); });
    });

    /* <grid-slider> caches its slides in an IntersectionObserver at connect
       time; after a reshuffle it must be re-created so arrows and is-visible
       track the cards actually on screen (same as relewise-recommendations.js). */
    var slider = scope.querySelector('grid-slider');
    if (slider) {
      var fresh = document.createElement('grid-slider');
      [].slice.call(slider.attributes).forEach(function (a) { fresh.setAttribute(a.name, a.value); });
      while (slider.firstChild) fresh.appendChild(slider.firstChild);
      slider.replaceWith(fresh);
    }
  }

  var TYPES = { personal: personal, popular: popular, relevance: relevance, sort: sort };

  function orderScope(scope) {
    var started = performance.now();
    var cards = cardsOf(scope);
    var type = scope.getAttribute('data-relewise-order');
    var run = TYPES[type];
    if (cards.length < 2 || !run) { ready(scope); return; }

    var opt = {
      collectionId: scope.getAttribute('data-collection-id') || '',
      take: parseInt(scope.getAttribute('data-take'), 10) || cards.length,
      page: Math.max(1, parseInt(scope.getAttribute('data-page'), 10) || 1),
      days: parseInt(scope.getAttribute('data-days'), 10) || 30,
      location: scope.getAttribute('data-location') || 'Collection Order',
      sort: scope.getAttribute('data-sort') || '',
      restrict: scope.getAttribute('data-restrict') || '',
      ids: cards.map(function (c) { return c.getAttribute('data-relewise-id') || ''; }).filter(Boolean),
    };
    var pageOnly = type === 'sort' || opt.restrict === 'page';

    userPromise
      .then(function (user) {
        var anonymous = typeof Relewise.userIsAnonymous === 'function' ? Relewise.userIsAnonymous(user) : true;
        /* Personal has nothing to rank on for a visitor Relewise cannot
           identify — popular within the same set is the honest stand-in. */
        if (type === 'personal' && anonymous) return popular(user, opt);
        return run(user, opt).then(function (handles) {
          return handles.length || type !== 'personal' ? handles : popular(user, opt);
        });
      })
      .then(function (handles) {
        if (pageOnly) {
          /* Never fetch when ranking the page's own set — it is the contract. */
          var onPage = {};
          cards.forEach(function (c) { onPage[handleOf(c)] = true; });
          handles = handles.filter(function (h) { return onPage[h]; });
        }
        if (!handles.length) return;
        return merge(handles, cards).then(function (nodes) {
          if (performance.now() - started > RENDER_BUDGET_MS) return;
          render(scope, cards, nodes);
        });
      })
      .catch(function () { /* keep Shopify's order */ })
      .then(function () { ready(scope); });
  }

  claim().forEach(orderScope);

  /* PLP grid swaps: the observer fires on the theme's innerHTML replacement
     (and on our own render(), where claim() then finds nothing new). */
  var grid = document.querySelector('[data-products-grid]');
  if (grid) {
    new MutationObserver(function () {
      claim().forEach(orderScope);
    }).observe(grid, { childList: true });
  }
})();
